import React, { useMemo, useState } from 'react';
import Page from 'components/Page';

import { Alert, Button, Card, Col, Descriptions, Result, Row } from 'antd';
import { ShoppingCartOutlined, WarningOutlined } from '@ant-design/icons';
import Table from 'components/Table';
import Drawer from 'components/Drawer';
import { dayjs } from 'shared/utils/date';
import StatusTag from 'components/StatusTag';
import SettingsList from 'components/Settings/SettingsList';
import { LICENSE_SETTINGS } from 'shared/constants/settings';
import { EDIT_LICENSE } from 'shared/constants/permissions';
import useUser from 'hooks/useUser';
import HubspotStatus from 'components/HubspotStatus';
import { LicenseInterval, Module } from 'shared/services/prisma/types';
import useDataSource from 'hooks/useDataSource';
import { renderDate } from 'components/Table/renderers';
import { FormattedMessage, FormattedRelativeTime } from 'react-intl';
import selectUnit from 'shared/utils/selectUnit';
import { dateFormat } from 'shared/utils/formatter';
import { visitLimitOptions } from 'shared/constants/visitLimits';
import SettingLabel from 'components/Settings/SettingLabel';
import { exceededLicenseLimit, getDataSourceLicenseTitle, getDataSourceLicenseUniqueTitle, hasLicenseLimit, isLicenseFree, isLicenseTrial, reachedLicenseLimit } from 'shared/utils/license';
import LicenseLimit from 'components/LicenseLimit';
import globalMessages from 'translations/messages';
import ChangeLicense from './ChangeLicense';

const extraFields = `
  hubspotDealRecordId      
  customer { id paymentType }
  owner { id email }
  licenses {
    id
    startDate
    endDate        
    renewalDate
    interval
    settings
    license {
      id
      title
      uniqueTitle
      level
    }
    modules {
      id
      module 
      title
      uniqueTitle
      level
    }        
  }      
`;

function getUsageStatus(usage) {
  if (exceededLicenseLimit(usage.current, usage.limit)) {
    return 'EXCEEDED_LIMIT';
  }
  if (reachedLicenseLimit(usage.current, usage.limit)) {
    return 'REACHED_LIMIT';
  }
  if (exceededLicenseLimit(usage.estimated, usage.limit)) {
    return 'ESTIMATED_EXCEEDED_LIMIT';
  }
  if (reachedLicenseLimit(usage.estimated, usage.limit)) {
    return 'ESTIMATED_REACHED_LIMIT';
  }
  return 'OK';
}

const usageColumns = [{
  dataIndex: 'setting',
  title: 'Name',
  render: setting => <SettingLabel setting={setting} />,
  defaultSortOrder: 'descend',
}, {
  dataIndex: 'period',
  title: 'Period',
  render: (period, record) => `${dateFormat(record.startDate)} - ${dateFormat(record.endDate)}`,
}, {
  dataIndex: 'limit',
  title: 'Limit',
  type: 'number',
  render: limit => <LicenseLimit limit={limit} />,
}, {
  dataIndex: 'current',
  title: 'Current',
  type: 'number',
}, {
  dataIndex: 'estimated',
  title: 'Estimated',
  type: 'number',
}, {
  dataIndex: 'status',
  title: 'Status',
  render: status => <StatusTag status={status} />,
  align: 'center',
}];

// const orderColumns = [{
//   dataIndex: 'createdAt',
//   title: 'Date',
//   type: 'date',
//   defaultSortOrder: 'descend',
// }, {
//   dataIndex: 'orderNumber',
//   title: 'Order number',
//   render: (orderNumber) => `#${orderNumber}`,
// }, {
//   dataIndex: 'amount',
//   title: 'Amount',
//   type: 'number',
//   render: (amount, record) => amount && <CurrencyFormat currency={record.currency} value={amount} />,
// }, {
//   dataIndex: 'paymentStatus',
//   title: 'Payment status',
//   align: 'center',
//   render: (status, record) => record.amount > 0 && <StatusTag status={status} />,
// }];

const LegacyLicense = ({ license }) => {
  let visitLimit = license?.settings.matomoMonthlyVisitLimit;

  if (hasLicenseLimit(visitLimit)) {
    visitLimit = visitLimitOptions.find(o => o.value === visitLimit)?.label;
  } else {
    visitLimit = 'Unlimited';
  }

  return (
    <Descriptions column={1}>
      <Descriptions.Item label="Visits/month">{visitLimit}</Descriptions.Item>
      <Descriptions.Item label="Dedicated analyst">{license.settings.dedicatedAnalyst ? 'Yes' : 'No'}</Descriptions.Item>
      <Descriptions.Item label="Unlimited heatmaps and recordings">{license.settings.unlimitedHeatmaps ? 'Yes' : 'No'}</Descriptions.Item>
    </Descriptions>
  );
};

const LicenseCard = ({ title, license, onShowDetails, isSuperUser }) => {
  const addons = license.modules?.filter(m => m.module === Module.Addon);

  const isTrial = isLicenseTrial(license);

  let subscription = license.interval === LicenseInterval.Monthly ? 'Monthly' : 'Yearly';

  if (isTrial) {
    subscription = <FormattedMessage {...globalMessages.trial} />;
  }

  return (
    <Card title={title} extra={onShowDetails && <Button onClick={onShowDetails}>Full details</Button>}>
      <Row>
        <Col span={12}>
          <Descriptions column={1}>
            <Descriptions.Item label="Subscription">{subscription}</Descriptions.Item>
            <Descriptions.Item label="Period">{`${dateFormat(license.startDate)} - ${dateFormat(license.endDate)}`}</Descriptions.Item>
            {!isTrial && <Descriptions.Item label="Automatic renewal">{license.renewalDate ? renderDate(license.renewalDate) : 'Off'}</Descriptions.Item>}
            <Descriptions.Item label="Products">{getDataSourceLicenseTitle(license)}</Descriptions.Item>
            {isSuperUser && <Descriptions.Item label="Product versions">{getDataSourceLicenseUniqueTitle(license)}</Descriptions.Item>}
          </Descriptions>
        </Col>
        <Col span={12}>
          {license.license && <LegacyLicense license={license} />}
          {addons?.length > 0 && (
          <Descriptions title="Addons" column={addons.length}>
            {addons.map(addon => <Descriptions.Item key={addon.id}>{addon.title}</Descriptions.Item>)}
          </Descriptions>
          )}
        </Col>
      </Row>

    </Card>
  );
};

const ManageLicense = () => {
  const [showDetails, setShowDetails] = useState(false);
  const [showChangeLicense, setShowChangeLicense] = useState(false);
  const { dataSource, loading, hasDataSourcePermission, refetch } = useDataSource({ extraFields, includeLicenseUsage: true });
  const { isSuperUser } = useUser();

  const canChange = hasDataSourcePermission(EDIT_LICENSE);

  const { activeLicense, currentUsage } = dataSource || {};

  const usage = useMemo(() => currentUsage?.map(p => ({
    ...p,
    status: getUsageStatus(p),
  })), [currentUsage]);

  const actionLimitReached = usage?.some(u => u.setting === 'actionLimit' && reachedLicenseLimit(u.current, u.limit));
  const actionLimitEstimatedReached = !actionLimitReached && usage?.some(u => reachedLicenseLimit(u.estimated, u.limit));
  const isFree = isLicenseFree(activeLicense);
  const isTrial = isLicenseTrial(activeLicense);
  const upcomingLicense = dataSource?.licenses.find(l => dayjs(l.startDate).isAfter() && l.id !== activeLicense?.id);
  const isEnding = activeLicense && !upcomingLicense && !activeLicense.renewalDate;

  const actions = dataSource && [
    isSuperUser && <HubspotStatus key="hubspot" hubspotDealRecordId={dataSource.hubspotDealRecordId} />,
    canChange && (
    <Button type="primary" key="change" icon={<ShoppingCartOutlined />} onClick={() => setShowChangeLicense(true)}>
      Buy new license
    </Button>
    ),
  ];

  return (
    <Page loading={loading}>
      <Page.Header
        title="License & Usage"
        extra={actions}
      />
      {upcomingLicense && (
      <Alert
        message={(
          <>
            Your current license will be replaced by a new license on
            {' '}
            {dateFormat(upcomingLicense.startDate)}
          </>
        )}
        type="info"
        showIcon
      />
      )}

      {isEnding && isTrial && (
      <Alert
        message="You are currently on a trial license. Upgrade your license before the trial ends to avoid interruption."
        type="warning"
        showIcon
      />
      )}

      {isEnding && !isTrial && (
      <Alert
        message={(
          <>
            Your current license ends
            {' '}
            <FormattedRelativeTime {...selectUnit(new Date(activeLicense.endDate))} />
          </>
        )}
        type="warning"
        showIcon
      />
      )}

      {activeLicense ? (
        <LicenseCard title="Active license" license={activeLicense} onShowDetails={canChange && (() => setShowDetails(activeLicense))} isSuperUser={isSuperUser} />
      ) : (
        <Card title="Active license">
          <Result
            icon={<WarningOutlined />}
            title="No active license"
            extra={(
              <Button type="primary" icon={<ShoppingCartOutlined />} onClick={() => setShowChangeLicense(true)}>
                Buy new license
              </Button>
            )}
          />
        </Card>
      )}

      {upcomingLicense && (
        <LicenseCard title="Upcoming license" license={upcomingLicense} onShowDetails={canChange && (() => setShowDetails(upcomingLicense))} isSuperUser={isSuperUser} />
      )}

      <Card style={{ marginTop: 16 }} title="Current usage">
        {isFree ? (
          <>
            {actionLimitReached && <Alert style={{ marginBottom: '1em' }} message="You have reached your action limit for the current period. Upgrade your license to continue collecting data." type="error" showIcon /> }
            {actionLimitEstimatedReached && <Alert style={{ marginBottom: '1em' }} message="You are estimated to reach your action limit before the current period ends. Upgrade your license to avoid interruption." type="warning" showIcon />}
          </>
        ) : (
          <>
            {actionLimitReached && <Alert style={{ marginBottom: '1em' }} message="You have reached your action limit for the current period and will be charged for any extra usage. Upgrade your license to get the lowest price." type="warning" showIcon />}
            {actionLimitEstimatedReached && <Alert style={{ marginBottom: '1em' }} message="You are estimated to reach your action limit before the current period ends and will be charged for extra usage. Upgrade your license to get the lowest price." type="warning" showIcon />}
          </>
        )}

        <Table
          dataSource={usage}
          columns={usageColumns}
        />
      </Card>

      <Drawer
        title="License details"
        open={showDetails}
        onClose={() => setShowDetails(false)}
        footer={false}
      >
        {showDetails && <SettingsList settings={LICENSE_SETTINGS} settingsValues={showDetails.settings} hideEmpty />}
      </Drawer>
      <ChangeLicense open={showChangeLicense} onCancel={() => setShowChangeLicense(false)} onChange={refetch} />
    </Page>
  );
};

export default ManageLicense;
